// Centralised soft-ask + denial copy for every OS permission.
//
// Option A: the WEB side fills tokens and SENDS this copy to the native shell
// inside the REQUEST_PERMISSION message. The native shell renders the soft-ask
// and the denial recovery itself — the web app shows no permission UI.
//
// Tokens:
//   {Agent} → the receptionist's name (Sophia / Nora / ...) — known on web only
//   {App}   → the app name shown in the OS Settings path
//
// `key` values match the `permission`fd string the native bridge expects.
// NOTE: CALENDAR is an OAuth connect flow, not an OS runtime permission.

export const PERMISSION_COPY = {
  MICROPHONE: {
    title: "Talk to {Agent} live",
    body:
      "To have a real conversation with your receptionist, we need your " +
      "microphone — that's how you'll hear exactly how she'll handle your " +
      "customers. Nothing is recorded.",
    allowLabel: "Allow Microphone",
    denyLabel: "Not now",
    denied:
      "Without microphone access you can't talk to {Agent} on a live test " +
      "call — the best way to hear her in action. You can keep setting up, " +
      "but to test her by voice, enable Microphone in Settings → {App} → " +
      "Microphone.",
  },

  CONTACTS: {
    title: "Let {Agent} recognize your callers",
    body:
      "She'll greet people you know by name and screen the ones you don't. " +
      "We only read names and numbers to recognize callers — nothing is " +
      "stored, shared, or used for marketing.",
    allowLabel: "Allow Contacts",
    denyLabel: "Not now",
    denied:
      "Without contact access, {Agent} can't tell your regulars from unknown " +
      "callers — so everyone is treated as a stranger and your VIPs can't be " +
      "sent straight to you. Turn on caller recognition anytime in Settings → " +
      "{App} → Contacts.",
  },

  // OAuth — not an OS permission.
  CALENDAR: {
    title: "Let {Agent} book appointments for you",
    body:
      "Connect your calendar so she schedules jobs directly during a call — " +
      "no double-bookings, no back-and-forth. She only adds and checks " +
      "appointment times; she never reads your personal events for anything " +
      "else.",
    allowLabel: "Connect Calendar",
    denyLabel: "Not now",
    denied:
      "Without calendar access, {Agent} can still answer calls and take " +
      "messages, but she can't book appointments automatically — you'd " +
      "schedule them yourself from the call summary. Connect anytime in " +
      "Settings (or via Google sign-in) to turn on automatic booking.",
    isOAuth: true,
  },

  // Ask ONLY after the first real call completes.
  NOTIFICATIONS: {
    title: "Never miss what {Agent} does for you",
    body:
      "Get a ping the moment she catches a call or books a job — so you " +
      "always know she's working, even when you're not looking.",
    allowLabel: "Turn On Notifications",
    denyLabel: "Maybe later",
    denied:
      "Without notifications you won't get real-time alerts when {Agent} " +
      "answers calls or books appointments — you'd have to open the app to " +
      "check. We'll keep important updates coming by email/SMS, and you can " +
      "turn on push anytime in Settings → {App} → Notifications.",
  },

  // On-demand, when tapping "Take a photo".
  CAMERA: {
    title: "Take a photo for your {App} profile",
    body: "To take a photo for your {App} profile, we need camera access.",
    allowLabel: "Allow Camera",
    denyLabel: "Not now",
    denied:
      "No problem — you can skip the photo or pick one from your gallery " +
      "instead. To use the camera, enable it in Settings → {App} → Camera.",
  },

  // Prefer the no-permission Photo Picker; this copy is only for full-library access.
  PHOTOS: {
    title: "Choose an image from your gallery",
    body: "To choose an image from your gallery, we need access to your photos.",
    allowLabel: "Allow Photos",
    denyLabel: "Not now",
    denied:
      "You can still take a new photo with the camera. To pick from your " +
      "gallery, enable Photos in Settings → {App} → Photos.",
  },

  LOCATION: {
    title: "Find your business faster",
    body:
      "Allow location and we'll show businesses near you to pick from. " +
      "Prefer not to? Just enter your zip code — works the same.",
    allowLabel: "Use My Location",
    denyLabel: "I'll enter my zip",
    denied:
      "No problem — enter your business zip or address and we'll find you " +
      "(this is often more accurate anyway, since you might not be at your " +
      "business right now). You can enable Location later in Settings → " +
      "{App} → Location for auto-detect.",
  },
};

// Replace {Agent} / {App} tokens in any copy string.
export const applyTokens = (
  text,
  { agent = "your receptionist", app = "the app" } = {}
) =>
  (text || "")
    .replace(/\{Agent\}/g, agent)
    .replace(/\{App\}/g, app);

// Get a fully-tokenised copy block to send to native.
export const getPermissionCopy = (permission, tokens) => {
  const c = PERMISSION_COPY[permission];
  if (!c) return null;
  return {
    title: applyTokens(c.title, tokens),
    body: applyTokens(c.body, tokens),
    allowLabel: c.allowLabel,
    denyLabel: c.denyLabel,
    denied: applyTokens(c.denied, tokens),
    isOAuth: !!c.isOAuth,
  };
};
